<template>
  <ChartCard v-if="!pageLoading">
    <ChartHeader label="Hiring Team" />
    <div class="team-list-header mt-4">
      <div class="team-list-inner-header pb-2">
        <div
          class="d-flex align-center justify-start list-name cursor-pointer"
          @click="() => updateSort('name')"
        >
          <span>Name</span>
          <SortArrow
            current-sort="name"
            :selected-sort="sortRootWord"
            down-arrow-sort-direction="asc"
            :selected-sort-direction="hiringTeamSortDirection"
          />
        </div>
        <div
          class="d-flex align-center justify-start list-team cursor-pointer"
          @click="() => updateSort('team')"
        >
          <span>Team</span>
          <SortArrow
            current-sort="team"
            :selected-sort="sortRootWord"
            down-arrow-sort-direction="desc"
            :selected-sort-direction="hiringTeamSortDirection"
          />
        </div>
      </div>
    </div>
    <div class="team-list-container" :class="{ paginated: hiringTeamTotalPages > 1 }">
      <v-list class="team-list">
        <DpplListItem
          v-for="{
            first_name: firstName,
            last_name: lastName,
            email,
            role_type: roleType,
          } in hiringTeam"
          :key="email"
          :ripple="false"
        >
          <template #left>
            <div class="left-column">
              <div class="name mb-1">
                <h5>{{ `${firstName}${lastName ? ` ${lastName}` : ''}` }}</h5>
              </div>
              <div class="email">
                <span v-if="email">{{ email }}</span>
              </div>
            </div>
          </template>
          <template #right>
            <div class="right-column">
              <span>{{ roleType }}</span>
            </div>
          </template>
        </DpplListItem>
      </v-list>
    </div>
    <div v-if="hiringTeamTotalPages > 1" class="pagination-container">
      <Pagination
        class="mt-0 pagination"
        :model-value="hiringTeamPage"
        :length="hiringTeamTotalPages"
        @update:model-value="(value) => updatePagination(value)"
      />
    </div>
  </ChartCard>
  <RequisitionReportTeamSkeleton v-else />
</template>

<script>
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { mapState, mapActions } from 'pinia';
import DpplListItem from '@/components/datapeople/DpplListItem.vue';
import { useAppLoading } from '@/stores/app/loading';
import RequisitionReportTeamSkeleton from '@/components/requisitions/skeletonLoaders/RequisitionReportTeamSkeleton.vue';
import Pagination from '@/components/shared/Pagination.vue';
import SortArrow from '@/components/shared/SortArrow.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';

export default {
  name: 'RequisitionReportTeam',
  components: {
    RequisitionReportTeamSkeleton,
    Pagination,
    SortArrow,
    DpplListItem,
    ChartHeader,
    ChartCard,
  },
  emits: ['requisitions-tracking-event'],
  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useRequisitionReport, [
      'hiringTeam',
      'hiringTeamTotalPages',
      'hiringTeamPage',
      'hiringTeamSort',
      'hiringTeamSortDirection',
      'reqUuid',
      'title',
      'authorized',
    ]),
    requisition() {
      const {
        reqUuid: uuid = '',
        title = '',
      } = this;

      return {
        uuid,
        title,
      };
    },
    sortRootWord() {
      return this.hiringTeamSort.startsWith('-') ? this.hiringTeamSort.slice(1) : this.hiringTeamSort;
    },
  },
  methods: {
    ...mapActions(useRequisitionReport, ['updateHiringTeamData']),
    async updatePagination(page) {
      await this.updateHiringTeamData({ page });
    },
    async updateSort(sort) {
      if (this.sortRootWord === sort) {
        if (this.hiringTeamSort.startsWith('-')) {
          await this.updateHiringTeamData({ sort: this.sortRootWord });
        } else {
          await this.updateHiringTeamData({ sort: `-${this.sortRootWord}` });
        }
      } else {
        const defaultPrepend = sort === 'team' ? '-' : '';

        await this.updateHiringTeamData({ sort: `${defaultPrepend}${sort}` });
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.loader {
  height: 459px;
  border-radius: 10px;
  flex: 1;
  .shadow();
}

.team-list-container {
  height: 350px;
  max-height: 350px;
  width: 100%;
  padding: 0px 24px;

  &.paginated {
    height: 304px;
    max-height: 304px;

    .team-list.v-list {
      height: 304px;
      max-height: 304px;
    }
  }
}

.team-list.v-list {
  height: 350px;
  max-height: 350px;
  width: 100%;
  overflow-y: auto;
  border-radius: 0px 0px 10px 10px;

  .v-list-item {
    min-height: 62px;
  }
}

.left-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0px 24px 0px 0px;
}

.right-column {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px 24px;
  border-left: 2px solid @stroke-default;
  width: 104px;

  span {
    font-size: 12px;
    line-height: 16px;
    font-weight: 300;
    text-align: left;
  }
}

.name h5 {
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  max-width: 470px;
  text-align: left;
}

.email {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  span {
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    color: @text-darkest;
  }
}

.pagination-container {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px 24px;
}

.pagination {
    width: calc(100% - 130px);
    display: flex;
    align-items: center;
    justify-content: flex-end;

  :deep(.v-pagination) {
    width: auto;
  }
}

:deep(.dppl-list-item).v-list-item {
  cursor: default;

  &:hover {
    box-shadow: none;
  }
}

.team-list-header {
  width: 100%;
  padding: 0px 24px;
}

.team-list-inner-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid @stroke-default;
}

.list-name {
  width: calc(100% - 104px);
  text-align: left;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.list-team {
  width: 104px;
  text-align: left;
  padding-left: 24px;
  color: @text-darkest;
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
}

.team-list-inner-header > div:hover :deep(.sort-arrow):not(.active) {
  opacity: .5;
}
</style>
